import showToast from '@Common/toast/showToast';
import { fileAPI } from '@Lib/axios/file';
import { fileTypeFromBuffer } from 'file-type';

export interface UploadResult {
  key: string;
  fileName: string;
}

export async function getMimeType(file: File): Promise<string> {
  const uint8Array = new Uint8Array(await file.arrayBuffer());
  const type = (await fileTypeFromBuffer(uint8Array))?.ext;

  switch (type) {
    case 'md':
      return 'text/markdown';
    case 'json':
      return 'application/json';
    case 'csv':
      return 'text/csv';
    case 'xml':
      return 'application/xml';
    case 'yaml':
    case 'yml':
      return 'application/x-yaml';
    case 'txt':
      return 'text/plain';
    case 'html':
      return 'text/html';
    case 'pdf':
      return 'application/pdf';
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'gif':
      return 'image/gif';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'webp':
      return 'image/webp';
    default:
      return file.type || 'application/octet-stream';
  }
}

export interface FileTypeInfo {
  type: 'image' | 'pdf' | 'text' | 'document';
  icon: 'Image' | 'FilePdf' | 'FileText';
  bgColor: string;
  iconColor: string;
}

export const getFileTypeInfo = (fileName: string): FileTypeInfo => {
  const lowerFileName = fileName.toLowerCase();

  if (lowerFileName.endsWith('.pdf')) {
    return {
      type: 'pdf',
      icon: 'FilePdf',
      bgColor: 'bg-red-100',
      iconColor: 'text-red-600',
    };
  } else if (
    lowerFileName.endsWith('.doc') ||
    lowerFileName.endsWith('.docx') ||
    lowerFileName.endsWith('.xls') ||
    lowerFileName.endsWith('.xlsx') ||
    lowerFileName.endsWith('.html')
  ) {
    return {
      type: 'document',
      icon: 'FileText',
      bgColor: 'bg-orange-100',
      iconColor: 'text-orange-600',
    };
  } else if (
    lowerFileName.endsWith('.md') ||
    lowerFileName.endsWith('.json') ||
    lowerFileName.endsWith('.txt') ||
    lowerFileName.endsWith('.csv') ||
    lowerFileName.endsWith('.xml') ||
    lowerFileName.endsWith('.yaml') ||
    lowerFileName.endsWith('.yml')
  ) {
    return {
      type: 'text',
      icon: 'FileText',
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600',
    };
  } else if (
    lowerFileName.endsWith('.gif') ||
    lowerFileName.endsWith('.jpg') ||
    lowerFileName.endsWith('.jpeg') ||
    lowerFileName.endsWith('.png') ||
    lowerFileName.endsWith('.webp')
  ) {
    return {
      type: 'image',
      icon: 'Image',
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
    };
  } else {
    return {
      type: 'document',
      icon: 'FileText',
      bgColor: 'bg-gray-100',
      iconColor: 'text-gray-600',
    };
  }
};

export async function handleAIAttachmentUpload(
  files: File[],
  logout: () => void,
  setProcessing: (v: boolean) => void,
  source?: string,
  onProgress?: (completed: number, total: number) => void
): Promise<MultipleFileUploadResult> {
  const api = fileAPI(logout);
  const attachments: UploadResult[] = [];
  const failedFiles: string[] = [];

  try {
    setProcessing(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      try {
        const mimeType = await getMimeType(file);
        const { uploadUrl, key } = await api.getUploadUrl(
          mimeType,
          file.name,
          source
        );

        await api.uploadFile(uploadUrl, file, mimeType);

        attachments.push({
          key,
          fileName: file.name,
        });

        if (onProgress) {
          onProgress(i + 1, files.length);
        }

        if (i < files.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        failedFiles.push(file.name);
      }
    }

    return {
      attachments,
      success: failedFiles.length === 0,
      failedFiles,
    };
  } catch (error) {
    console.error('Error in multiple file upload:', error);
    return {
      attachments,
      success: false,
      failedFiles: files.map((f) => f.name),
    };
  } finally {
    setProcessing(false);
  }
}

export const generateFileReferenceTags = (
  attachments: UploadResult[]
): string[] => {
  return attachments.map((attachment) => {
    const fileTypeInfo = getFileTypeInfo(attachment.fileName);
    return `[${fileTypeInfo.type}:${attachment.fileName}]`;
  });
};

export const createMessageWithAttachments = (
  promptContent: string,
  attachments: UploadResult[]
): string => {
  let messageWithAttachments =
    promptContent ||
    `Please analyze the following ${attachments.length} file(s)`;

  const fileRefs = generateFileReferenceTags(attachments);
  messageWithAttachments += '\n\n' + fileRefs.join('\n');

  return messageWithAttachments;
};

export const showFileProcessingToast = (attachmentCount: number): void => {
  if (attachmentCount > 1) {
    showToast.info(
      `Processing ${attachmentCount} files. This may take a moment...`
    );
  }
};

export const handleFileUploadResult = (result: {
  success: boolean;
  attachments: UploadResult[];
  failedFiles: string[];
}): void => {
  if (result.success) {
    showToast.success(
      `Successfully uploaded ${result.attachments.length} file(s)`
    );
  } else {
    showToast.error(
      `Failed to upload some files: ${result.failedFiles.join(', ')}`
    );
  }
};

export const validatePromptBeforeSend = (
  promptContent: string,
  attachments: UploadResult[]
): boolean => {
  if (!promptContent && attachments.length === 0) {
    showToast.error('You cannot send empty prompts.', 'empty-prompt');
    return false;
  }
  return true;
};

export const handleFileSelectionUpload = async (
  files: File[],
  logout: () => void,
  setProcessing: (v: boolean) => void,
  setAttachments: React.Dispatch<React.SetStateAction<UploadResult[]>>,
  clearFileInput?: () => void,
  source?: string
): Promise<void> => {
  if (files.length === 0) return;

  try {
    const result = await handleAIAttachmentUpload(
      files,
      logout,
      setProcessing,
      source
    );

    handleFileUploadResult(result);

    if (result.attachments.length > 0) {
      setAttachments((prev) => [...prev, ...result.attachments]);
    }
  } catch (error) {
    console.error('File upload error:', error);
    handleFileUploadResult({
      success: false,
      attachments: [],
      failedFiles: files.map((f) => f.name),
    });
  }

  if (clearFileInput) {
    clearFileInput();
  }
};

export const removeAttachmentAtIndex = (
  index: number,
  setAttachments: React.Dispatch<React.SetStateAction<UploadResult[]>>
): void => {
  setAttachments((prev) => prev.filter((_, i) => i !== index));
};

export const clearAllAttachments = (
  setAttachments: React.Dispatch<React.SetStateAction<UploadResult[]>>
): void => {
  setAttachments([]);
};

export interface MultipleFileUploadResult {
  attachments: UploadResult[];
  success: boolean;
  failedFiles: string[];
}
